import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar } from '@ant-design/plots';
import { Typography, Button, Tag, Flex, Divider, Empty } from '@app/ui';
import * as format from '@app/utils/formatters';

const colors8 = [
    '#1783FF',
    '#00C9C9',
    '#F0884D',
    '#D580FF',
    '#7863FF',
    '#60C42D',
    '#BD8F24',
    '#FF80CA',
];

const AudienceLanguageChart = ({ selectedAccount }: any) => {
    const { t } = useTranslation('ownedSocial');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const topLanguages = useMemo(() => {
        const languageMap = selectedAccount?.insights?.language || {};

        return Object.entries(languageMap)
            .sort(([, a], [, b]) => Number(b) - Number(a))
            .slice(0, 8)
            .map(([segmentName, value], index) => ({
                segmentName,
                value,
                color: colors8[index % colors8.length],
            }));
    }, [selectedAccount]);

    useEffect(() => {
        setSelectedIds(topLanguages.map(lang => lang.segmentName));
    }, [topLanguages]);

    const toggleItem = (id: string) => {
        setSelectedIds(prev => {
            if (prev.includes(id)) {
                // Prevent if only 2 items are currently selected
                if (prev.length <= 2) return prev;
                return prev.filter(x => x !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const chartData = useMemo(
        () =>
            topLanguages
                .filter(item => selectedIds.includes(item.segmentName))
                .map(item => ({
                    ...item,
                })),
        [topLanguages, selectedIds],
    );

    const maximumRalativeLabelRate = useMemo(() => {
        if (chartData.length) {
            try {
                return (
                    Math.max(
                        ...chartData
                            .filter(item =>
                                selectedIds.includes(item.segmentName),
                            )
                            .map(item => Number(item.value)),
                    ) / 10
                );
            } catch (error) {
                return 1;
            }
        }
        return 1;
    }, [chartData, selectedIds]);

    const config = {
        height: 450,
        data: chartData,
        xField: 'segmentName',
        yField: 'value',
        autoFit: true,
        legend: false,
        axis: {
            x: {
                grid: true,
                labelFontSize: 16,
                labelFill: '#5E6066',
                labelOpacity: 1,
            },
            y: {
                title: t('audience.followers'),
                titleFontSize: 16,
                titleFill: '#5E6066',
                titleStroke: 'transparent',
                labelFontSize: 16,
                labelFill: '#5E6066',
                labelOpacity: 1,
                labelFormatter: value => format.decimal(value),
                labelTransform: 'rotate(0)',
            },
        },
        style: {
            fill: ({ segmentName }: any) => {
                const item = topLanguages.find(
                    lang => lang.segmentName === segmentName,
                );
                return item?.color || '#ccc';
            },
            fontFamily: 'pnregular',
        },
        tooltip: {
            title: () => t('audience.followers'),
            items: [
                d => ({
                    name: d.segmentName,
                    value: format.decimal(d.value),
                }),
            ],
        },
        label: {
            text: data => {
                return format.decimal(data.value);
            },
            style: {
                textAlign: d =>
                    d.value > maximumRalativeLabelRate ? 'right' : 'left',
                dx: d => (d.value > maximumRalativeLabelRate ? -10 : 10),
            },
        },
    };

    if (!topLanguages.length) {
        return (
            <div className="audience-charts-wrapper">
                <Typography.Title
                    level={4}
                    style={{ fontFamily: 'pnbold', marginBottom: 0 }}
                >
                    {t('audience.language.topLanguagesByFollowers')}
                </Typography.Title>
                <Divider style={{ borderColor: '#0000000f', margin: 0 }} />
                <div className="audience-empty">
                    <Empty
                        description={t('audience.language.noLanguageData')}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="audience-charts-wrapper">
            <Typography.Title
                level={4}
                style={{ fontFamily: 'pnbold', marginBottom: 0 }}
            >
                {t('audience.language.topLanguagesByFollowers')}
            </Typography.Title>
            <Divider style={{ borderColor: '#0000000f', margin: 0 }} />
            <div className="language-charts-body">
                <div className="audience-charts-left">
                    <Flex className="ranker-list" vertical gap={4}>
                        {topLanguages.map((item, index) => {
                            const isSelected = selectedIds.includes(
                                item.segmentName,
                            );
                            return (
                                <Button
                                    key={item.segmentName}
                                    style={{
                                        fontFamily: 'pnregular',
                                        justifyContent: 'space-between',
                                        padding: '24px 16px',
                                        borderColor: 'transparent',
                                        borderRadius: 0,
                                        backgroundColor: isSelected
                                            ? 'rgba(186, 224, 255, 0.70)'
                                            : 'transparent',
                                    }}
                                    type={isSelected ? 'primary' : undefined}
                                    onClick={() => toggleItem(item.segmentName)}
                                >
                                    <Flex>
                                        <Typography.Text
                                            style={{
                                                whiteSpace: 'nowrap',
                                                marginRight: 16,
                                                width: 20,
                                            }}
                                        >
                                            {index + 1}.
                                        </Typography.Text>
                                        <Typography.Text
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: 200,
                                                textAlign: 'left',
                                            }}
                                        >
                                            {item.segmentName}
                                        </Typography.Text>
                                    </Flex>
                                    <Tag
                                        color={item.color}
                                        style={{
                                            width: 60,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {format.shortDecimal(item.value, 1)}
                                    </Tag>
                                </Button>
                            );
                        })}
                    </Flex>
                </div>
                <div className="audience-charts-right">
                    <Bar {...config} />
                </div>
            </div>
        </div>
    );
};

export default AudienceLanguageChart;
